import { BetSlipType } from '@bpos';

import { BetslipType } from '../../../core/betslip-type';
import { isBetBuilderPick } from '../../../core/utils';
import { SlipId } from '../../bet-generation/models';
import { getBetBuilderSlipId, getComboSlipId, getTeaserSlipId, isSingleSlipId } from '../../bet-generation/services/slip.utils';
import {
    FailedSlipDetailsType,
    IFailedSlipBetBuilderDetails,
    IFailedSlipDetails,
    IFailedSlipResult,
    IFailedSlipTeaserDetails,
    ISucceededSlipResult,
} from '../../betplacement/models';
import { isFailedSlip } from '../../betplacement/utils';
import { BetPlacementError } from '../../validation/errors/bet-placement-error';
import { BetslipTypePickErrors, emptyPickErrors } from '../../validation/state';
import { IFailedSlipErrors } from '../models';
import { mapBetslipType } from '../services/result.utils';

interface PlacementError {
    error: BetPlacementError;
    slipId?: string;
}

interface SlipPlacementError {
    error: BetPlacementError;
    slipId: string;
}

export function isBetBuilderSucceededSlip(slip: ISucceededSlipResult): boolean {
    if (slip.picks.length !== 1) {
        return false;
    }

    const clientPick = slip.picks[0].clientPick;

    return !!clientPick && isBetBuilderPick(clientPick);
}

export function isTeaserSucceededSlip(slip: ISucceededSlipResult): boolean {
    if (slip.picks.length <= 1) {
        return false;
    }

    return slip.picks.every((pick) => !!pick.isTeaserBet);
}

export function getFailedSlipErrors(slips: IFailedSlipResult[], isLinear: boolean, isSportcastAsCombo: boolean): IFailedSlipErrors {
    const failedSlips = slips.filter(isFailedSlip);

    const allPlacementErrors = failedSlips.flatMap((slip) => getErrorsFromSlip(slip, isLinear, isSportcastAsCombo));

    const slipPlacementErrors = allPlacementErrors.filter((error): error is SlipPlacementError => !!error.slipId);
    const betslipPlacementErrors = allPlacementErrors.filter((error) => !error.slipId);

    const pickErrors = getPickErrors(failedSlips, isLinear, isSportcastAsCombo);
    const slipErrors = getSlipErrors(slipPlacementErrors);
    const betslipErrors = betslipPlacementErrors.map((error) => error.error);

    return { pickErrors, betslipErrors, slipErrors };
}

function getSlipErrors(placementSlipErrors: SlipPlacementError[]): Record<SlipId, BetPlacementError[]> {
    return placementSlipErrors.reduce(
        (aggrSlipErrors, { error, slipId }) => {
            if (!aggrSlipErrors[slipId]) {
                aggrSlipErrors[slipId] = [];
            }

            aggrSlipErrors[slipId].push(error);

            return aggrSlipErrors;
        },
        {} as Record<SlipId, BetPlacementError[]>,
    );
}

function getPickErrors(failedSlips: IFailedSlipResult[], isLinear: boolean, isSportcastAsCombo: boolean): BetslipTypePickErrors {
    return failedSlips.reduce((aggr, current) => {
        const placementBetslipType = mapBetslipType(current.betType)!;

        const betslipType = getLocalBetslipType(isLinear, placementBetslipType, current, isSportcastAsCombo);

        if (!aggr[betslipType]) {
            aggr[betslipType] = {};
        }

        for (const [key, value] of Object.entries(current.picks)) {
            aggr[betslipType][key] = value.errors;
        }

        return aggr;
    }, emptyPickErrors());
}

function getErrorsFromSlip(slip: IFailedSlipResult, isLinear: boolean, isSportcastAsCombo: boolean): PlacementError[] {
    return slip.errors.map((error) => ({ error, slipId: getErrorSlipId(error, slip, isLinear, isSportcastAsCombo) }));
}

function getErrorSlipId(error: BetPlacementError, slip: IFailedSlipResult, isLinear: boolean, isSportcastAsCombo: boolean): string | undefined {
    if (!error.slipId) {
        return undefined;
    }

    const slipDetails = slip.details;

    if (!isLinear) {
        const showSingleSportcastSlipErrorAsCombo =
            slip.betType === BetSlipType.Single && isSingleSlipId(error.slipId) && isBetBuilderFailedSlipDetails(slipDetails) && isSportcastAsCombo;

        return showSingleSportcastSlipErrorAsCombo ? getComboSlipId() : error.slipId;
    }

    if (isBetBuilderFailedSlipDetails(slipDetails)) {
        return getBetBuilderSlipId(slipDetails.betBuilderPickId);
    }

    if (isTeaserFailedSlipDetails(slipDetails) && slipDetails.isTeaserBet) {
        return getTeaserSlipId();
    }

    return error.slipId;
}

function getLocalBetslipType(isLinear: boolean, betBetslipType: BetslipType, slip: IFailedSlipResult, isSportcastAsCombo: boolean): BetslipType {
    if (!isLinear) {
        const showSingleSportcastPickErrorAsCombo =
            betBetslipType === BetslipType.Single &&
            slip.betType === BetSlipType.Single &&
            isBetBuilderFailedSlipDetails(slip.details) &&
            isSportcastAsCombo;

        return showSingleSportcastPickErrorAsCombo ? BetslipType.Combo : betBetslipType;
    }

    if (isBetBuilderFailedSlipDetails(slip.details)) {
        return BetslipType.BetBuilder;
    }

    if (isTeaserFailedSlipDetails(slip.details)) {
        return BetslipType.Teaser;
    }

    return betBetslipType;
}

function isBetBuilderFailedSlipDetails(slipDetails: IFailedSlipDetails | undefined): slipDetails is IFailedSlipBetBuilderDetails {
    return !!slipDetails && slipDetails.type === FailedSlipDetailsType.BetBuilder;
}

function isTeaserFailedSlipDetails(slipDetails: IFailedSlipDetails | undefined): slipDetails is IFailedSlipTeaserDetails {
    return !!slipDetails && slipDetails.type === FailedSlipDetailsType.Teaser;
}
